import { useRef, useState } from 'react';
import type React from 'react';
import { store } from '@/ui/store';
import { Popover, rectOf, type AnchorRect } from '@/ui/Popover';
import { prettyField } from '@/data/classify';
import { groupFields } from '@/giraffe/database';
import { DB_AGGS } from '@/ui/dbAggs';
import type { DbColumn } from '@/model/types';

/**
 * The ▾ on a database title, and what it offers: how this column summarises,
 * whether the database groups by it, and the column itself.
 *
 * On the title rather than in a pane because that is where the question comes
 * up — looking at a column of areas and wanting it summed, or at a column of
 * unit types and wanting one row per type.
 */
export function DbTitleButton({ dbId, index }: { dbId: string; index: number }): JSX.Element {
  const [at, setAt] = useState<AnchorRect | null>(null);
  const ref = useRef<HTMLButtonElement | null>(null);
  return (
    <>
      <button
        ref={ref}
        className="gt-db-title-btn"
        title="Group, summarise or remove this column"
        onPointerDown={(e) => {
          e.stopPropagation();
          setAt(rectOf(ref.current));
        }}
      >
        ▾
      </button>
      {at ? <DbTitleMenu dbId={dbId} index={index} anchor={at} onClose={() => setAt(null)} /> : null}
    </>
  );
}

function DbTitleMenu({
  dbId,
  index,
  anchor,
  onClose,
}: {
  dbId: string;
  index: number;
  anchor: AnchorRect;
  onClose: () => void;
}): JSX.Element | null {
  const db = store.database(dbId);
  const column = db?.columns[index];
  if (!db || !column) return null;

  /*
   * Pointerdown, not click, as in the chip menu (grid/Grid.tsx ChipOption): the
   * popover is a portal, so a press bubbles through the React tree to the grid,
   * which re-renders under it and the click never lands.
   */
  const run = (fn: () => void) => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onClose();
    fn();
  };
  const setColumns = (columns: DbColumn[], label: string): void => store.updateDatabase(dbId, { columns }, label);
  const fields = groupFields(db);
  const grouped = fields.includes(column.field);
  const name = prettyField(column.field);

  return (
    <Popover anchor={anchor} onClose={onClose} className="gt-menu-list" swallowOutsidePress>
      <div onPointerDown={(e) => e.stopPropagation()}>
      <div className="gt-menu-heading">{name}</div>
      {grouped ? (
        <button
          className="gt-menu-item"
          onPointerDown={run(() => {
            // Dropping the outer field promotes the inner one.
            const rest = fields.filter((f) => f !== column.field);
            store.updateDatabase(dbId, { groupBy: rest[0] ?? null, thenBy: null }, 'Ungroup');
          })}
        >
          <span className="gt-menu-name">{fields.length > 1 ? `Stop grouping by ${name}` : 'Ungroup'}</span>
        </button>
      ) : (
        <button
          className="gt-menu-item"
          onPointerDown={run(() => store.updateDatabase(dbId, { groupBy: column.field, thenBy: null }, 'Group by'))}
        >
          <span className="gt-menu-name">Group by {name}</span>
        </button>
      )}
      {!grouped && fields.length > 0 ? (
        <button
          className="gt-menu-item"
          onPointerDown={run(() => store.updateDatabase(dbId, { thenBy: column.field }, 'Then group by'))}
        >
          <span className="gt-menu-name">
            {fields.length > 1 ? `Then by ${name}, not ${prettyField(fields[1]!)}` : `Then by ${name}`}
          </span>
        </button>
      ) : null}
      <button
        className="gt-menu-item"
        onPointerDown={run(() => store.updateDatabase(dbId, { collapsed: !db.collapsed }, db.collapsed ? 'Show sub-rows' : 'Hide sub-rows'))}
      >
        <span className="gt-menu-name">{db.collapsed ? 'Show sub-rows' : 'Hide sub-rows'}</span>
      </button>

      <div className="gt-menu-heading">Summary</div>
      {DB_AGGS.map((a) => (
        <button
          key={a.id}
          className={`gt-menu-item${column.agg === a.id ? ' is-on' : ''}`}
          onPointerDown={run(() =>
            setColumns(
              db.columns.map((c, j) => (j === index ? { ...c, agg: a.id } : c)),
              a.label
            )
          )}
        >
          <span className="gt-menu-name">
            {column.agg === a.id ? '✓ ' : ''}
            {a.label}
          </span>
        </button>
      ))}

      <div className="gt-menu-heading">Column</div>
      <button
        className="gt-menu-item"
        onPointerDown={run(() =>
          setColumns([...db.columns.slice(0, index + 1), { ...column }, ...db.columns.slice(index + 1)], 'Duplicate column')
        )}
      >
        <span className="gt-menu-name">Duplicate column</span>
      </button>
      {db.columns.length > 1 ? (
        <button
          className="gt-menu-item"
          onPointerDown={run(() => {
            const columns = db.columns.filter((_, j) => j !== index);
            // Grouping by a field no column shows would leave the groups unlabelled.
            const kept = groupFields(db).filter((f) => columns.some((c) => c.field === f));
            store.updateDatabase(dbId, { columns, groupBy: kept[0] ?? null, thenBy: kept[1] ?? null }, 'Remove column');
          })}
        >
          <span className="gt-menu-name">Remove column</span>
        </button>
      ) : null}
      <button className="gt-menu-item" onPointerDown={run(() => void store.selectDbObjects(dbId, null))}>
        <span className="gt-menu-name">Select all on the map</span>
      </button>
      <button className="gt-menu-item" onPointerDown={run(() => store.openDbWizard(dbId))}>
        <span className="gt-menu-name">Edit criteria and columns…</span>
      </button>
      <button className="gt-menu-item" onPointerDown={run(() => store.deleteDatabase(dbId))}>
        <span className="gt-menu-name gt-menu-danger">Delete database</span>
      </button>
      </div>
    </Popover>
  );
}

/**
 * Right-click on a database sub-row: the objects behind it, on the map.
 *
 * A sub-row is not a cell to format — it IS an object (or, grouped, a group of
 * them), so the useful thing to offer is finding it: select it in Giraffe, or
 * fly there.
 */
export function DbRowMenu({
  x,
  y,
  dbId,
  sub,
  label,
  onClose,
}: {
  x: number;
  y: number;
  dbId: string;
  sub: number;
  label: string;
  onClose: () => void;
}): JSX.Element | null {
  const db = store.database(dbId);
  if (!db) return null;
  const here = store.dbObjectIds(dbId, sub).length;
  const all = store.dbObjectIds(dbId, null).length;
  const run = (fn: () => void) => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onClose();
    fn();
  };
  const noun = (n: number) => `${n} ${n === 1 ? 'item' : 'items'}`;

  return (
    <Popover anchor={{ x, y }} gap={0} onClose={onClose} closeOnScroll swallowOutsidePress className="gt-menu-list">
      <div onPointerDown={(e) => e.stopPropagation()}>
        <div className="gt-menu-heading">
          Row {label}
          {db.groupBy ? ' · group' : ''}
        </div>
        <button className="gt-menu-item" disabled={here === 0} onPointerDown={run(() => void store.selectDbObjects(dbId, sub))}>
          <span className="gt-menu-name">Select {noun(here)} on the map</span>
        </button>
        <button className="gt-menu-item" disabled={here === 0} onPointerDown={run(() => void store.zoomToDbObjects(dbId, sub))}>
          <span className="gt-menu-name">Zoom to {here === 1 ? 'it' : 'them'}</span>
        </button>
        <div className="gt-menu-heading">Whole database</div>
        <button className="gt-menu-item" disabled={all === 0} onPointerDown={run(() => void store.selectDbObjects(dbId, null))}>
          <span className="gt-menu-name">Select all {noun(all)}</span>
        </button>
        <button className="gt-menu-item" onPointerDown={run(() => store.openDbWizard(dbId))}>
          <span className="gt-menu-name">Edit criteria and columns…</span>
        </button>
        <button className="gt-menu-item" onPointerDown={run(() => store.deleteDatabase(dbId))}>
          <span className="gt-menu-name gt-menu-danger">Delete database</span>
        </button>
      </div>
    </Popover>
  );
}
